import { Link } from "react-router-dom";
import { Card, DataState, PageHeader, StatCard } from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface OrgMetrics {
  hubsOnline: number;
  hubsTotal: number;
  sessionsThisWeek: number;
  activeDriversThisMonth: number;
  pendingBookings: number;
  venueCount: number;
}

export function OrgDashboard() {
  const { currentOrg, venues, venuesLoading } = useOrgContext();
  const metrics = useQuery<OrgMetrics>(
    currentOrg ? `/api/organisations/${currentOrg.id}/metrics` : null,
  );

  if (!currentOrg) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={currentOrg.name}
        subtitle="Across-venue ops-health snapshot."
      />

      <DataState loading={metrics.loading} error={metrics.error}>
        {metrics.data && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard label="Sessions this week" value={metrics.data.sessionsThisWeek} />
            <StatCard label="Active drivers (30d)" value={metrics.data.activeDriversThisMonth} />
            <StatCard
              label="Hubs online"
              value={`${metrics.data.hubsOnline} / ${metrics.data.hubsTotal}`}
              tone={
                metrics.data.hubsTotal > 0 && metrics.data.hubsOnline < metrics.data.hubsTotal
                  ? "amber"
                  : "default"
              }
            />
            <StatCard
              label="Pending bookings"
              value={metrics.data.pendingBookings}
              tone={metrics.data.pendingBookings > 0 ? "amber" : "default"}
            />
            <StatCard label="Venues" value={metrics.data.venueCount} />
          </div>
        )}
      </DataState>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Venues</h2>
          <Link
            to={`/org/${currentOrg.slug}/venues`}
            className="text-sm font-medium text-primary hover:underline"
          >
            All venues →
          </Link>
        </div>

        <DataState
          loading={venuesLoading}
          empty={!venuesLoading && venues.length === 0}
          emptyMessage="No venues in this organisation yet."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {venues.map((venue) => (
              <Link key={venue.id} to={`/org/${currentOrg.slug}/venue/${venue.slug}`}>
                <Card className="p-5 hover:border-primary hover:shadow-md transition cursor-pointer">
                  <div className="font-semibold text-gray-900">{venue.name}</div>
                  <div className="text-xs text-gray-500 font-mono mt-0.5">{venue.slug}</div>
                  {venue.address && (
                    <div className="text-sm text-gray-600 mt-2 truncate">{venue.address}</div>
                  )}
                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                      {venue.licencePlan ?? "no plan"}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                      {venue.channel ?? "stable"}
                    </span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </DataState>
      </div>
    </div>
  );
}
